import React, { useEffect, useMemo, useState } from "react";
import {
  ContentLayout,
  SpaceBetween,
  Box,
  Container,
  PropertyFilter,
  PropertyFilterProps,
} from "@cloudscape-design/components";
import { useNavigate } from "react-router-dom";
import ActivityTable from "../Dashboard/ActivityTable";
import { TrajectoryLoader } from "../common/Loaders";
import { ConfirmationModal } from "../common/Modals";
import { useActivityFiltering } from "./hooks/useActivityFiltering";
import { useGlobalStore } from "../../store/global-store";
import { useWorkflowStore } from "../../store/workflow-store";
import { useGetAllWorkflowExecutions } from "../../api/hooks";
import { transformToActivityItem } from "../../utils/activityTransformUtils";
import { sanitizeForDisplay, validateRequiredIds } from "../../utils/security";
import { getOptionByValue } from "../../utils/optionUtils";
import { ActivityItem } from "./types";
import { ROUTES } from "../../constants";
import { PROPERTY_FILTER_I18N_STRINGS } from "../../constants/cloudscape-i18n";
import { Button } from "../common/Button";
import "./all-activities.scss";
import { logger } from '../../utils/logger';

const AllActivities: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSpaceId, ensureSpaceSelected } = useGlobalStore();
  const { workflows, fetchWorkflows, isLoading: isWorkflowsLoading, isDeletingExecution, deleteWorkflowExecution } = useWorkflowStore();
  const { data: executionsData, isLoading: isExecutionsLoading, error } = useGetAllWorkflowExecutions(selectedSpaceId || undefined);

  const [deleteModal, setDeleteModal] = useState<{
    visible: boolean;
    activity: ActivityItem | null;
  }>({ visible: false, activity: null });

  const [deletedExecutionIds, setDeletedExecutionIds] = useState<Set<string>>(new Set());

  // Clear deleted execution IDs when space changes
  useEffect(() => {
    setDeletedExecutionIds(new Set());
  }, [selectedSpaceId]);

  // Ensure space is selected and fetch workflows
  useEffect(() => {
    const initializeData = async () => {
      try {
        await ensureSpaceSelected();
      } catch (err) {
        logger.error('Failed to initialize space:', err as Error);
      }
    };

    initializeData();
  }, [ensureSpaceSelected]);

  // Fetch workflows when selectedSpaceId is available
  useEffect(() => {
    if (selectedSpaceId) {
      fetchWorkflows(selectedSpaceId);
    }
  }, [selectedSpaceId, fetchWorkflows]);

  // Transform API data to ActivityItem format with optimistic deletion filtering
  const activitiesData = useMemo((): ActivityItem[] => {
    if (!executionsData?.results) {
      return [];
    }

    try {
      return executionsData.results
        .filter(execution => !deletedExecutionIds.has(execution.id!))
        .map(execution => {
          const workflow = workflows?.find(w => w.id === execution.workflowId);
          return transformToActivityItem(execution, workflow);
        });
    } catch (err) {
      logger.error('Failed to transform workflow executions:', err as Error);
      return [];
    }
  }, [executionsData, workflows, deletedExecutionIds]);

  const [filteringQuery, setFilteringQuery] = useState<PropertyFilterProps.Query>({ tokens: [], operation: 'and' });

  const {
    filteredActivities,
    filteringProperties,
    filteringOptions,
    isLoading: isFilterLoading,
  } = useActivityFiltering({
    activities: activitiesData,
    filteringQuery,
    isServerSide: false,
  });

  const handleDownload = (activity: ActivityItem) => {
    logger.info("Download activity:", { reportName: activity.reportName });
    // TODO: Implement download functionality
  };

  const handleDeleteClick = (activity: ActivityItem) => {
    setDeleteModal({ visible: true, activity });
  };

  const handleDeleteConfirm = async () => {
    const { activity } = deleteModal;
    if (!activity || !validateRequiredIds(selectedSpaceId, activity.workflowId, activity.workflowExecutionId)) {
      logger.error('Missing required IDs for workflow execution deletion');
      setDeleteModal({ visible: false, activity: null });
      return;
    }

    try {
      await deleteWorkflowExecution(
        selectedSpaceId!, 
        activity.workflowId!, 
        activity.workflowExecutionId!
      );

      setDeletedExecutionIds(prev => new Set(prev).add(activity.workflowExecutionId!));
      
      setDeleteModal({ visible: false, activity: null });
    } catch (error) {
      logger.error('Failed to delete workflow execution:', error as Error);
      setDeleteModal({ visible: false, activity: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ visible: false, activity: null });
  };

  const handleDelete = (activity: ActivityItem) => {
    handleDeleteClick(activity);
  };

  const handleBack = () => {
    navigate(ROUTES.DASHBOARD);
  };

  // Show loading state while fetching executions or workflows
  const isLoading = isExecutionsLoading || isWorkflowsLoading || isFilterLoading;

  return (
    <ContentLayout
      defaultPadding
      header={
        <div className="activities-header">
          <div className="header-content">
            <h1 className="header-title">All Activities</h1>
            <div className="header-description-row">
              <p className="header-description">View and manage the outputs of your workflows</p>
              <Button
                variant="secondary"
                icon="arrow-left"
                onClick={handleBack}
              >
                Back
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Activities List */}
        {isLoading ? (
          <Box>
            <div className="loading-state-loader">
              <TrajectoryLoader size="140px" speed="2s" />
              <div className="loading-message">
                Loading activities...
              </div>
            </div>
          </Box>
        ) : error ? (
          <Box textAlign="center" padding="xxl">
            <Box variant="strong" textAlign="center" color="inherit">
              Error loading activities
            </Box>
            <Box variant="p" padding={{ bottom: "s" }} textAlign="center" color="inherit">
              Failed to load workflow executions. Please try refreshing the page.
            </Box>
          </Box>
        ) : (
          <Container
            header={
              <PropertyFilter
                query={filteringQuery}
                onChange={({ detail }) => setFilteringQuery(detail)}
                filteringProperties={filteringProperties}
                filteringOptions={filteringOptions}
                countText={`${filteredActivities.length} ${filteredActivities.length === 1 ? 'match' : 'matches'}`}
                i18nStrings={{
                  ...PROPERTY_FILTER_I18N_STRINGS,
                  filteringPlaceholder: "Search by workflow type, name, or status",
                }}
              />
            }
          >
            <ActivityTable 
              items={filteredActivities} 
              loading={isLoading}
              showAllActions={true}
              onDownload={handleDownload}
              onDelete={handleDelete}
            />
          </Container>
        )}
      </SpaceBetween>

      <ConfirmationModal
        visible={deleteModal.visible}
        title="Delete Activity"
        message={`Are you sure you want to delete activity "${sanitizeForDisplay(deleteModal.activity?.reportName)}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="primary"
        loading={isDeletingExecution}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </ContentLayout>
  );
};

export default AllActivities;
